import { useEffect, useMemo, useState, useRef } from "react";
import {
  parseTabular as parseTabularBytes,
  type Diagnostic as ImportDiagnostic,
  type ParseOptions,
} from "@proof/tabular";
import {
  Upload,
  Play,
  CheckCircle2,
  Download,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  X,
  FileSpreadsheet,
} from "lucide-react";
import {
  bulkFieldEligible,
  createBulkTemplate,
  convertBulkCell,
  effectiveFields,
  bulkRequestSchema,
  validateBulkRows,
  validateSubmission,
  imageUrl,
  type BulkSource,
  type Context,
  type Project,
  type Values,
  bytesHash,
  parseProject,
} from "@proof/core";
import { ProofCanvas } from "@proof/ui";
import { useBulkContext } from "./useBulkContext";
import { DynamicSourceParameters } from "@proof/ui";
import { Select, Button, Control, Empty } from "./components";
import type { ProofCanvasProps } from "@proof/ui";
import {
  bindBulkGenerationServices,
  bindBulkSessionStore,
  snapshotBulkSession,
  snapshotBulkPreparedImages,
  snapshotBulkBatch,
  snapshotBulkSubmission,
  snapshotBulkDefinition,
  readBulkArtifact,
  downloadBulkArtifact,
  type BulkGenerationServices,
  type BulkSessionStore,
  type BulkDownload,
  type BulkJobView as JobView,
  type BulkBatchView as BulkView,
} from "./bulk-generation";
export interface BulkGenerationProps {
  project: Project;
  versionId?: string;
  services: BulkGenerationServices;
  proofServices?: Pick<
    ProofCanvasProps,
    "assetUrl" | "loadAsset" | "transformImage" | "loadFont"
  >;
  contextProfiles?: { id: string; label: string }[];
  initialContextProfile?: string;
  session?: BulkSessionStore;
  notify?: (message: string) => void;
  showNotices?: boolean;
  onBatchChange?: (batch: BulkView) => void;
  onDownload?: (
    artifact: BulkDownload,
    signal: AbortSignal,
  ) => void | Promise<void>;
}
/** All project, version and adapter changes cancel the previous workflow before it can publish results. */
export function BulkGeneration(props: BulkGenerationProps) {
  const sequence = useRef(0);
  const setup = useMemo(() => {
    const revision = ++sequence.current;
    try {
      return {
        revision,
        project: parseProject(structuredClone(props.project)),
        services: bindBulkGenerationServices(props.services),
        session: props.session && bindBulkSessionStore(props.session),
      };
    } catch {
      return {
        revision,
        error:
          "Provide a document and bulk-generation services for this workspace.",
      };
    }
  }, [
    props.project,
    props.versionId,
    props.services,
    props.proofServices,
    props.session,
    props.contextProfiles,
    props.initialContextProfile,
  ]);
  if (!setup.services || !setup.project)
    return <p role="alert">{setup.error}</p>;
  return (
    <BulkWorkspace
      key={setup.revision}
      {...props}
      project={setup.project}
      services={setup.services}
      session={setup.session}
    />
  );
}
export interface Sheet {
  name: string;
  headers: string[];
  rows: string[][];
  delimiter?: string;
  encoding?: BulkSource["encoding"];
  headerRow?: number;
  sourceRows?: number[];
  originalHeaders?: string[];
  lastRow?: number;
  rowCount?: number;
}
interface BulkRow {
  row: number;
  state: string;
  diagnostics?: { message: string }[];
  job?: JobView;
  key?: string;
  retryable?: boolean;
}
function BulkWorkspace({
  project: p,
  versionId,
  services,
  proofServices,
  contextProfiles,
  initialContextProfile,
  session,
  onBatchChange,
  onDownload,
  notify: announce,
  showNotices = true,
}: BulkGenerationProps) {
  const root = useRef<HTMLDivElement>(null);
  const lifetime = useRef(new AbortController());
  const operation = useRef<AbortController | undefined>(undefined);
  const [notice, setNotice] = useState("");
  const notify = (message: string) => {
    if (!lifetime.current.signal.aborted) {
      setNotice(message);
      announce?.(message);
    }
  };
  const newOperation = () => {
    operation.current?.abort();
    operation.current = new AbortController();
    return AbortSignal.any([operation.current.signal, lifetime.current.signal]);
  };
  const deliver = async (artifact: BulkDownload, signal: AbortSignal) => {
    signal.throwIfAborted();
    if (onDownload) await onDownload(artifact, signal);
    else downloadBulkArtifact(artifact, root.current!.ownerDocument);
    signal.throwIfAborted();
  };
  const downloadBlob = (blob: Blob, filename: string) =>
    void deliver(
      { blob, filename, kind: "template" },
      lifetime.current.signal,
    ).catch((error) => notify(error.message));
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => {
      controller.abort();
      operation.current?.abort();
    };
  }, []);
  const [sheets, setSheets] = useState<Sheet[]>([]),
    [sheetIndex, setSheet] = useState(0),
    [mapping, setMapping] = useState<Record<string, string>>({}),
    [confirmed, setConfirmed] = useState(false),
    [rows, setRows] = useState<BulkRow[]>([]),
    [index, setIndex] = useState(0),
    [busy, setBusy] = useState(false),
    [delimiter, setDelimiter] = useState(""),
    [encoding, setEncoding] =
      useState<NonNullable<ParseOptions["encoding"]>>("auto"),
    [format, setFormat] = useState<NonNullable<ParseOptions["format"]>>("auto"),
    [headerRowOption, setHeaderRowOption] = useState(0),
    [parseDirty, setParseDirty] = useState(false),
    [importDiagnostics, setImportDiagnostics] = useState<ImportDiagnostic[]>(
      [],
    ),
    [profileId, setProfile] = useState(p.outputs[0].id),
    [partId, setPart] = useState(""),
    [filter, setFilter] = useState("all"),
    [source, setSource] =
      useState<
        Omit<
          BulkSource,
          "headers" | "columns" | "worksheetIndex" | "rowCount" | "headerRow"
        >
      >(),
    [batch, setBatch] = useState<BulkView>(),
    [savedBatches, setSavedBatches] = useState<BulkView[]>([]),
    [reviewProject, setReviewProject] = useState<Project>(),
    [reviewValues, setReviewValues] = useState<Values[]>(),
    [reviewContext, setReviewContext] = useState<Context>(),
    [pageIndices, setPageIndices] = useState<Record<number, number>>({}),
    [pageCount, setPageCount] = useState(1);
  const importedFile = useRef<File | undefined>(undefined);
  const proofProject = reviewProject || p;
  const previews = useMemo(() => {
    const input = proofServices;
    return {
      assetUrl: input?.assetUrl
        ? (id: string) => input.assetUrl!.call(input, id)
        : () =>
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
      loadAsset: input?.loadAsset
        ? (...args: Parameters<NonNullable<ProofCanvasProps["loadAsset"]>>) =>
            input.loadAsset!.apply(input, args)
        : input?.assetUrl
          ? undefined
          : async () => {
              throw new Error("Provide an asset loader for this document");
            },
      transformImage: input?.transformImage
        ? (
            ...args: Parameters<NonNullable<ProofCanvasProps["transformImage"]>>
          ) => input.transformImage!.apply(input, args)
        : undefined,
      loadFont: input?.loadFont
        ? (...args: Parameters<NonNullable<ProofCanvasProps["loadFont"]>>) =>
            input.loadFont!.apply(input, args)
        : async () => {
            throw new Error("Provide a font loader for this document");
          },
    };
  }, [proofServices]);
  const template = useMemo(() => {
    try {
      return {
        ...createBulkTemplate(proofProject, partId || undefined),
        error: "",
      };
    } catch (error) {
      return { columns: [], csv: "", error: (error as Error).message };
    }
  }, [proofProject, partId]);
  const [demoProfile, setDemoProfile] = useState(
    initialContextProfile || contextProfiles?.[0]?.id || "",
  );
  const host = useBulkContext(
      p,
      partId,
      demoProfile,
      !reviewProject,
      versionId,
      services,
    ),
    context = reviewProject ? reviewContext : host.context;
  const checkBatch = (raw: unknown) => snapshotBulkBatch(raw, p.id);
  useEffect(() => {
    const controller = new AbortController();
    void services
      .listBatches(controller.signal)
      .then((list) => {
        controller.signal.throwIfAborted();
        setSavedBatches(
          list.filter((b) => b.projectId === p.id).map(checkBatch),
        );
      })
      .catch((error) => {
        if (!controller.signal.aborted) notify(error.message);
      });
    return () => controller.abort();
  }, [p.id, services]);
  const sheet = sheets[sheetIndex],
    profile =
      proofProject.outputs.find((o) => o.id === profileId) ||
      proofProject.outputs[0];
  const inputFields = useMemo(
    () =>
      new Map(
        effectiveFields(proofProject, partId || undefined).map((field) => [
          field.id,
          field,
        ]),
      ),
    [proofProject, partId],
  );
  const rawValues = useMemo(
    () =>
      reviewValues ||
      (sheet
        ? sheet.rows.map((row) => {
            const result: Values = {};
            for (const [col, fieldId] of Object.entries(mapping)) {
              if (!fieldId) continue;
              const f = inputFields.get(fieldId);
              if (!f || !bulkFieldEligible(f)) continue;
              const value = row[Number(col)] || "";
              result[fieldId] = convertBulkCell(f, value);
            }
            return result;
          })
        : []),
    [sheet, mapping, inputFields, reviewValues],
  );
  const [preparedImages, setPreparedImages] = useState<{
    raw: Values[];
    values: Values[];
    project: Project;
    partId: string;
    context: Context | undefined;
    diagnostics: { fieldId?: string; code: string; message: string }[][];
  }>();
  const imageRequest = useRef<AbortController | undefined>(undefined);
  const imageInput = useRef({ raw: rawValues, project: p, partId, context });
  imageInput.current = { raw: rawValues, project: p, partId, context };
  useEffect(
    () => () => {
      imageRequest.current?.abort();
    },
    [rawValues, p, partId, context],
  );
  const imagesCurrent =
    preparedImages?.raw === rawValues &&
    preparedImages.project === p &&
    preparedImages.partId === partId &&
    preparedImages.context === context;
  const values = imagesCurrent ? preparedImages!.values : rawValues;
  useEffect(() => {
    if (preparedImages && !imagesCurrent && !reviewProject) setConfirmed(false);
  }, [preparedImages, imagesCurrent, reviewProject]);
  const validations = useMemo(
    () =>
      values.map((v, index) => {
        const checked = validateSubmission(
          proofProject,
          v,
          partId || undefined,
          context,
        );
        // URL import results describe the source rows; keep explaining them
        // in review even after generation changes the working project.
        if (
          imagesCurrent ||
          preparedImages?.diagnostics.length === values.length
        )
          checked.errors.push(
            ...preparedImages!.diagnostics[index]
              .filter((item) => item.code.startsWith("IMAGE_URL"))
              .map((item) => ({ ...item, severity: "error" as const })),
          );
        return checked;
      }),
    [values, proofProject, partId, context, imagesCurrent, preparedImages],
  );
  const progress = rows.filter(
    (r) =>
      r.job &&
      ["complete", "failed", "cancelled", "quarantined"].includes(r.job.state),
  ).length;
  const reconcileBatch = (raw: BulkView) => {
    const updated = checkBatch(raw);
    if (batch && updated.id !== batch.id)
      throw new Error("Batch identity changed");
    if (batch && updated.stateVersion < batch.stateVersion) return;
    setBatch(updated);
    setRows((current) =>
      current.map((row, i) => {
        const sourceRow =
          sheet?.sourceRows?.[i] || (sheet?.headerRow || 1) + i + 1;
        const stored = updated.rows.find((r) => r.sourceRow === sourceRow);
        return stored
          ? {
              ...row,
              key: stored.key,
              state: stored.state,
              retryable: stored.diagnostic?.retryable,
              diagnostics: stored.diagnostic ? [stored.diagnostic] : undefined,
              job: stored.job,
            }
          : row;
      }),
    );
  };
  useEffect(() => {
    if (!batch || !["pending", "running"].includes(batch.state)) return;
    const controller = new AbortController();
    let current = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const updated = checkBatch(
          await services.readBatch(batch.id, controller.signal),
        );
        if (current) reconcileBatch(updated);
      } catch (error) {
        if (current) notify((error as Error).message);
      } finally {
        if (current) timer = setTimeout(poll, 1000);
      }
    };
    timer = setTimeout(poll, 1000);
    return () => {
      current = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [batch?.id, batch?.state, sheet]);
  useEffect(() => {
    if (session && batch && sheet) {
      try {
        session.write({
          tenantId: p.tenantId,
          projectId: p.id,
          versionId,
          batchId: batch.id,
          sheets,
          sheetIndex,
          mapping,
          partId,
          profileId,
          source,
          index,
          pageIndices,
        });
      } catch {
        notify("Browser storage is full; server batch remains available");
      }
    }
  }, [
    batch?.id,
    sheets,
    sheetIndex,
    mapping,
    partId,
    profileId,
    index,
    pageIndices,
  ]);
  const restoreBatch = async (id: string) => {
    const signal = newOperation();
    setBusy(true);
    try {
      const [raw, rawDefinition] = await Promise.all([
        services.readBatch(id, signal),
        services.readDefinition(id, signal),
      ]);
      signal.throwIfAborted();
      const saved = checkBatch(raw),
        definition = snapshotBulkDefinition(rawDefinition, p, saved);
      if (saved.id !== id) throw new Error("Saved batch identity changed");
      let cached: any;
      try {
        const value = snapshotBulkSession(session?.read());
        if (
          value?.tenantId === p.tenantId &&
          value.projectId === p.id &&
          value.versionId === versionId &&
          value.batchId === id &&
          Array.isArray(value.sheets) &&
          value.sheets[value.sheetIndex]?.rows?.length <= 10000
        )
          cached = value;
      } catch {
        /* Server snapshots remain available without browser data. */
      }
      if (cached?.batchId === id) {
        setSheets(cached.sheets);
        setSheet(cached.sheetIndex);
        setMapping(cached.mapping);
        setSource(cached.source);
        setIndex(cached.index || 0);
        setPageIndices(cached.pageIndices || {});
        const local = cached.sheets[cached.sheetIndex];
        const fields = new Map(
          effectiveFields(definition.project, definition.partId).map(
            (field) => [field.id, field],
          ),
        );
        setReviewValues(
          local.rows.map((cells: string[], index: number) => {
            const sourceRow =
              local.sourceRows?.[index] || saved.source.headerRow + index + 1;
            const accepted = definition.rows.find(
              (row) => row.sourceRow === sourceRow,
            );
            if (accepted) return accepted.values;
            const values: Values = {};
            for (const [column, fieldId] of Object.entries(cached.mapping) as [
              string,
              string,
            ][]) {
              const field = fields.get(fieldId);
              if (field && bulkFieldEligible(field))
                values[fieldId] = convertBulkCell(
                  field,
                  cells[Number(column)] || "",
                );
            }
            return values;
          }),
        );
        setRows(
          local.rows.map((_: unknown, i: number) => ({
            row: i + 1,
            state: "invalid",
          })),
        );
      } else {
        const restoredSheet: Sheet = {
          name: "Saved batch",
          headers: saved.source.headers,
          headerRow: saved.source.headerRow,
          sourceRows: definition.rows.map((r) => r.sourceRow),
          rows: definition.rows.map((row) =>
            saved.mapping.columns.map((col) =>
              col.fieldId ? String(row.values[col.fieldId] ?? "") : "",
            ),
          ),
        };
        setSheets([restoredSheet]);
        setSheet(0);
        setMapping(
          Object.fromEntries(
            saved.mapping.columns
              .filter((c) => c.fieldId)
              .map((c) => [c.column, c.fieldId!]),
          ),
        );
        setReviewValues(definition.rows.map((r) => r.values));
        setRows(saved.rows.map((r, i) => ({ row: i + 1, state: r.state })));
        setIndex(0);
      }
      setReviewContext(definition.context);
      setReviewProject({
        ...definition.project,
        outputs: [definition.profile],
      });
      setProfile(definition.profile.id);
      setPart(definition.partId || "");
      setConfirmed(true);
      setBatch(saved);
      const local =
        cached?.batchId === id ? cached.sheets[cached.sheetIndex] : undefined;
      setRows((current) =>
        current.map((row, i) => {
          const sourceRow = local
            ? local.sourceRows?.[i] || saved.source.headerRow + i + 1
            : definition.rows[i].sourceRow;
          const stored = saved.rows.find((row) => row.sourceRow === sourceRow);
          return stored
            ? {
                ...row,
                key: stored.key,
                state: stored.state,
                job: stored.job,
                retryable: stored.diagnostic?.retryable,
                diagnostics: stored.diagnostic
                  ? [stored.diagnostic]
                  : undefined,
              }
            : row;
        }),
      );
    } catch (e) {
      if (!signal.aborted) notify((e as Error).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const cancelBatch = async () => {
    if (!batch || busy) return;
    const signal = newOperation();
    setBusy(true);
    try {
      const result = await services.cancelBatch(
        batch.id,
        batch.stateVersion,
        signal,
      );
      signal.throwIfAborted();
      reconcileBatch(result);
    } catch (error) {
      if (!signal.aborted) notify((error as Error).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const retryRow = async () => {
    const row = rows[index];
    if (!batch || !row?.key || busy) return;
    const signal = newOperation();
    setBusy(true);
    try {
      const result = await services.retryRows(
        batch.id,
        batch.stateVersion,
        [row.key],
        signal,
      );
      signal.throwIfAborted();
      reconcileBatch(result);
    } catch (error) {
      if (!signal.aborted) notify((error as Error).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const suggest = (sheet: Sheet) => {
    const mapping: Record<string, string> = {};
    const used = new Set<string>();
    let columns: ReturnType<typeof createBulkTemplate>["columns"] = [];
    try {
      columns = createBulkTemplate(p, partId || undefined).columns;
    } catch {
      /* Larger documents still allow manually mapping a bounded source. */
    }
    sheet.headers.forEach((h, i) => {
      const norm = (s: string) =>
        s
          .normalize("NFKC")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
      const raw = sheet.originalHeaders || sheet.headers;
      if (raw.filter((value) => value === raw[i]).length > 1) return;
      const exact = columns.filter(
        (column) => column.header === raw[i] && !used.has(column.fieldId),
      );
      if (
        !exact.length &&
        raw.filter((value) => norm(value) === norm(raw[i])).length > 1
      )
        return;
      const matches = effectiveFields(p, partId || undefined).filter(
        (f) =>
          bulkFieldEligible(f) &&
          !used.has(f.id) &&
          [f.key, f.label].some((key) => norm(key) === norm(h)),
      );
      const f =
        exact.length === 1
          ? p.fields.find((field) => field.id === exact[0].fieldId)
          : matches.length === 1
            ? matches[0]
            : undefined;
      if (f) {
        mapping[String(i)] = f.id;
        used.add(f.id);
      }
    });
    setMapping(mapping);
    setReviewProject(undefined);
    setReviewContext(undefined);
    setReviewValues(undefined);
    setBatch(undefined);
    setPageIndices({});
    setConfirmed(false);
    setRows([]);
    setIndex(0);
  };
  const load = async (file: File) => {
    const signal = newOperation();
    setBusy(true);
    try {
      if (file.size < 1 || file.size > 10000000)
        throw new Error("Choose a source file up to 10 MB");
      const bytes = await file.arrayBuffer();
      signal.throwIfAborted();
      const digest = bytesHash(new Uint8Array(bytes));
      setParseDirty(true);
      const parsed = await parseTabularBytes(new Uint8Array(bytes), {
        format,
        encoding,
        delimiter: delimiter || undefined,
        headerRow: headerRowOption || "auto",
        previewRows: 10000,
      });
      signal.throwIfAborted();
      importedFile.current = file;
      const imported: Sheet[] = parsed.sheets.map((sheet) => ({
        ...sheet,
        delimiter: parsed.delimiter,
        encoding: parsed.encoding,
        originalHeaders: sheet.headerCells.map((c) => c.value),
        lastRow: sheet.dataRange?.lastRow ?? sheet.headerRow,
      }));
      setImportDiagnostics(parsed.diagnostics);
      setParseDirty(false);
      setSource({
        sha256: digest,
        bytes: file.size,
        format:
          parsed.format === "delimited" && parsed.delimiter === "\t"
            ? "tsv"
            : parsed.format,
        encoding: parsed.encoding,
        delimiter: imported[0].delimiter || "",
      });
      setSheets(imported);
      setSheet(0);
      suggest(imported[0]);
      notify(
        `Loaded ${imported[0].rows.length} rows. Review mappings before generation.`,
      );
    } catch (e) {
      if (!signal.aborted) notify((e as Error).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const sourceContract = (cellValues: Values[]) => {
    if (!source || !sheet) throw new Error("Import a source file first");
    const headerRow = sheet.headerRow || 1;
    return bulkRequestSchema.parse({
      version: 1,
      source: {
        ...source,
        delimiter: sheet.delimiter ?? source.delimiter,
        headers: sheet.headers,
        columns: sheet.headers.length,
        headerRow,
        worksheetIndex: sheetIndex,
        rowCount: sheet.rowCount || sheet.rows.length,
        lastRow: sheet.lastRow,
        originalHeaders: sheet.originalHeaders,
      },
      mapping: {
        version: 1,
        confirmed: true,
        headerRow,
        worksheetIndex: sheetIndex,
        columns: sheet.headers.map((header, column) => ({
          column,
          header,
          confirmed: true,
          originalHeader: sheet.originalHeaders?.[column],
          disposition: mapping[column] ? "map" : "ignore",
          ...(mapping[column] ? { fieldId: mapping[column] } : {}),
        })),
      },
      rows: cellValues.map((value, i) => ({
        sourceRow: sheet.sourceRows?.[i] || headerRow + i + 1,
        values: value,
      })),
    });
  };
  const confirmMapping = async () => {
    const input = imageInput.current;
    setBusy(true);
    const controller = new AbortController();
    operation.current?.abort();
    operation.current = controller;
    imageRequest.current?.abort();
    imageRequest.current = controller;
    try {
      if (
        rawValues.some((row) =>
          Object.entries(row).some(
            ([id, value]) =>
              inputFields.get(id)?.type === "image" && imageUrl(value),
          ),
        )
      ) {
        if (!services.prepareImages)
          throw new Error(
            "Provide an image URL preparation service before confirming this mapping",
          );
        const rawPrepared = await services.prepareImages(
          {
            project: p,
            versionId,
            profile,
            contextProfile: demoProfile || undefined,
            partId: partId || undefined,
            contextHash: host.hash,
            sourceParameters: host.sourceParameters,
            batch: sourceContract(rawValues),
          },
          AbortSignal.any([controller.signal, lifetime.current.signal]),
        );
        controller.signal.throwIfAborted();
        lifetime.current.signal.throwIfAborted();
        const result = snapshotBulkPreparedImages(
          rawPrepared,
          sourceContract(rawValues),
        );
        const expected = sourceContract(rawValues).rows;
        if (
          result.rows.length !== expected.length ||
          result.rows.some((row, i) => row.sourceRow !== expected[i].sourceRow)
        )
          throw new Error(
            "Prepared image rows do not match the imported source",
          );
        if (
          controller.signal.aborted ||
          imageInput.current.raw !== input.raw ||
          imageInput.current.project !== input.project ||
          imageInput.current.partId !== input.partId ||
          imageInput.current.context !== input.context
        )
          return;
        setPreparedImages({
          ...input,
          values: result.rows.map((row) => row.values),
          diagnostics: result.rows.map((row) => row.diagnostics),
        });
        if (result.rows.some((row) => row.diagnostics.length))
          notify(
            "Some image URLs or records need attention. Review the row diagnostics before generation.",
          );
      }
      if (!controller.signal.aborted) setConfirmed(true);
    } catch (error) {
      if (!controller.signal.aborted) notify((error as Error).message);
    } finally {
      if (imageRequest.current === controller) setBusy(false);
    }
  };
  const start = async () => {
    if (!confirmed || parseDirty || busy) return;
    const signal = newOperation();
    setBusy(true);
    try {
      const contract = sourceContract(values);
      if (host.blocked)
        throw new Error("Refresh host values before generating");
      const assessed = validateBulkRows(
        p,
        contract,
        partId || undefined,
        context,
      );
      const valid = contract.rows.filter(
        (_, i) => !assessed[i].diagnostics.length,
      );
      if (!valid.length) throw new Error("No valid rows to generate");
      const submission = {
        project: p,
        versionId,
        profile,
        contextProfile: demoProfile || undefined,
        partId: partId || undefined,
        contextHash: host.hash,
        sourceParameters: host.sourceParameters,
        batch: { ...contract, rows: valid },
      };
      const result = snapshotBulkSubmission(
        await services.createBatch(submission, signal),
        submission,
      );
      signal.throwIfAborted();
      setBatch(result);
      setReviewContext(context);
      setReviewValues(values);
      setReviewProject({ ...p, outputs: [profile] });
      setRows(
        assessed.map((row, i) => {
          const stored = result.rows.find((r) => r.sourceRow === row.sourceRow);
          return {
            row: i + 1,
            state: row.diagnostics.length
              ? "invalid"
              : stored?.state || "pending",
            diagnostics: row.diagnostics.length
              ? row.diagnostics
              : stored?.diagnostic
                ? [stored.diagnostic]
                : [],
            key: stored?.key,
            job: stored?.job,
            retryable: stored?.diagnostic?.retryable,
          };
        }),
      );
      setSavedBatches((current) => [
        result,
        ...current.filter((b) => b.id !== result.id),
      ]);
      notify("Batch queued. Each row retains its proof and artifact status.");
    } catch (e) {
      if (!signal.aborted) notify((e as Error).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const eligible = (i: number) =>
    filter === "all" ||
    (filter === "invalid" && validations[i].errors.length > 0) ||
    (filter === "failed" &&
      ["failed", "quarantined"].includes(rows[i]?.job?.state || "")) ||
    (filter === "complete" && rows[i]?.job?.state === "complete");
  const navigate = (direction: number) => {
    for (let i = index + direction; i >= 0 && i < values.length; i += direction)
      if (eligible(i)) {
        setIndex(i);
        return;
      }
  };
  const downloadRecord = async (job: JobView) => {
    const signal = newOperation();
    setBusy(true);
    try {
      const blob = await readBulkArtifact(job, services, signal);
      await deliver(
        {
          blob,
          filename: job.artifact!.filename,
          kind: "record",
          batchId: batch?.id,
          jobId: job.id,
        },
        signal,
      );
    } catch (error) {
      if (!signal.aborted) notify((error as Error).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  const zip = async () => {
    const signal = newOperation();
    setBusy(true);
    try {
      const { zipSync } = await import("fflate");
      signal.throwIfAborted();
      const files: Record<string, Uint8Array> = {};
      let total = 0;
      for (const row of rows.filter(
        (row) =>
          row.job?.state === "complete" && row.job.artifact?.downloadAllowed,
      )) {
        const blob = await readBulkArtifact(row.job!, services, signal);
        total += blob.size;
        if (total > 200000000)
          throw new Error(
            "Batch ZIP exceeds 200 MB. Download records individually.",
          );
        files[
          `row-${String(row.row).padStart(4, "0")}-${row.job!.artifact!.filename}`
        ] = new Uint8Array(await blob.arrayBuffer());
      }
      signal.throwIfAborted();
      await deliver(
        {
          blob: new Blob([zipSync(files) as BlobPart], {
            type: "application/zip",
          }),
          filename: "proof-studio-batch.zip",
          kind: "zip",
          batchId: batch?.id,
        },
        signal,
      );
    } catch (error) {
      if (!signal.aborted) notify((error as Error).message);
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  useEffect(() => {
    if (batch) onBatchChange?.(structuredClone(batch));
  }, [batch]);
  return (
    <div className="bulk-page" ref={root}>
      {notice && showNotices && <p role="status">{notice}</p>}
      {!reviewProject && (
        <fieldset className="bulk-source-parameters" disabled={busy}>
          <DynamicSourceParameters
            project={p}
            partId={partId || undefined}
            values={host.sourceParameters}
            onChange={host.setSourceParameters}
          />
        </fieldset>
      )}
      <div className="page-intro">
        <div>
          <span className="eyebrow">ONE DESIGN, MANY STORIES</span>
          <h1>Bulk personalization</h1>
          <p>Map your data, review each proof, and generate a batch.</p>
        </div>
        <div className="bulk-import-actions">
          <Button
            disabled={!template.columns.length}
            onClick={() => {
              const name = [
                proofProject.name,
                proofProject.parts.find((part) => part.id === partId)?.name,
                "template",
              ]
                .filter(Boolean)
                .join("-")
                .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
                .slice(0, 150);
              downloadBlob(
                new Blob(["\ufeff", template.csv], {
                  type: "text/csv;charset=utf-8",
                }),
                `${name}.csv`,
              );
            }}
          >
            <Download size={15} /> Download template
          </Button>
          <label className="button primary">
            <Upload size={15} />
            Import data
            <input
              disabled={busy}
              className="sr-only"
              type="file"
              accept=".csv,.tsv,.txt,.xlsx"
              onChange={(e) => {
                if (e.target.files?.[0]) void load(e.target.files[0]);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>
      <section className="panel padded">
        <div className="control-grid">
          <Control label="Part">
            <Select
              value={partId}
              disabled={!!batch || busy}
              onChange={(e) => {
                setPart(e.target.value);
                setConfirmed(false);
                setMapping({});
                setRows([]);
              }}
            >
              <option value="">Main project</option>
              {proofProject.parts.map((part) => (
                <option key={part.id} value={part.id}>
                  {part.name}
                </option>
              ))}
            </Select>
          </Control>
          <Control label="File format">
            <Select
              disabled={busy}
              value={format}
              onChange={(e) => {
                setFormat(e.target.value as typeof format);
                setConfirmed(false);
                setParseDirty(true);
              }}
            >
              {["auto", "csv", "delimited", "xlsx"].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </Select>
          </Control>
          <Control label="Text encoding">
            <Select
              disabled={busy}
              value={encoding}
              onChange={(e) => {
                setEncoding(e.target.value as typeof encoding);
                setConfirmed(false);
                setParseDirty(true);
              }}
            >
              {["auto", "utf-8", "utf-16le", "utf-16be", "windows-1252"].map(
                (value) => (
                  <option key={value}>{value}</option>
                ),
              )}
            </Select>
          </Control>
          <Control
            label="Header row"
            note="Blank selects the first populated row."
          >
            <input
              type="number"
              min="1"
              max="1048576"
              value={headerRowOption || ""}
              onChange={(e) => {
                setHeaderRowOption(Number(e.target.value));
                setConfirmed(false);
                setParseDirty(true);
              }}
            />
          </Control>
          <Control
            label="Text delimiter"
            note="Blank detects comma, tab, semicolon or pipe."
          >
            <input
              value={delimiter}
              placeholder="Auto detect"
              onChange={(e) => {
                setDelimiter(e.target.value.slice(0, 1));
                setConfirmed(false);
                setParseDirty(true);
              }}
            />
          </Control>
        </div>
        <div className="bulk-template-guide">
          <p>
            {template.error ||
              (template.columns.length
                ? `${template.columns.length} template columns for ${proofProject.parts.find((part) => part.id === partId)?.name || "the main project"}. Add one data row per document, then import the completed CSV.`
                : "This form has no variables that can be entered through a bulk data file.")}
          </p>
          <p className="muted">
            Read-only, generated, map and other structured fields use the
            document configuration. Conditional fields are included; each row is
            checked against the form rules.
          </p>
          {!!template.columns.length && (
            <details>
              <summary>Template field guide</summary>
              <div className="bulk-template-table">
                <table>
                  <thead>
                    <tr>
                      <th>CSV column</th>
                      <th>Form field</th>
                      <th>Requirement</th>
                      <th>Accepted values</th>
                    </tr>
                  </thead>
                  <tbody>
                    {template.columns.map((column) => (
                      <tr key={column.fieldId}>
                        <td>
                          <code>{column.header}</code>
                        </td>
                        <td>
                          {column.label}
                          {column.help && <small>{column.help}</small>}
                        </td>
                        <td>
                          {column.required ? "Required" : "Optional"}
                          {column.conditional ? " when shown" : ""}
                        </td>
                        <td>
                          {column.choices.length
                            ? `${column.choices.join(", ")}${column.type === "checkbox" ? " (separate multiple values with ;)" : ""}`
                            : column.type === "checkbox"
                              ? "true or false"
                              : column.type}
                          {!column.required &&
                            " · Omit the column to keep its inherited value"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
        <Button
          disabled={!importedFile.current || busy}
          onClick={() => {
            if (importedFile.current) void load(importedFile.current);
          }}
        >
          Reparse source
        </Button>
        {parseDirty && sheet && (
          <p role="status">
            Reparse the source to apply changed options before confirming
            mappings.
          </p>
        )}
        {importDiagnostics.length > 0 && (
          <details>
            <summary>Import diagnostics ({importDiagnostics.length})</summary>
            {importDiagnostics.map((d, i) => (
              <p key={i}>
                {d.code}: {d.message}
                {d.location
                  ? ` · sheet ${d.location.sheet + 1}, ${d.location.address}`
                  : ""}
              </p>
            ))}
          </details>
        )}
      </section>
      {savedBatches.length > 0 && (
        <Control label="Saved batch">
          <Select
            disabled={busy}
            value={batch?.id || ""}
            onChange={(e) => {
              if (e.target.value) void restoreBatch(e.target.value);
            }}
          >
            <option value="">Choose a saved batch to resume</option>
            {savedBatches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.projectName} · {b.state} · {b.progress.total} rows
              </option>
            ))}
          </Select>
        </Control>
      )}
      <div className="bulk-stepper">
        <span className="active">
          1 <strong>Import & map</strong>
        </span>
        <span className={confirmed ? "active" : ""}>
          2 <strong>Review proofs</strong>
        </span>
        <span className={rows.length ? "active" : ""}>
          3 <strong>Generate batch</strong>
        </span>
      </div>
      {!sheet ? (
        <section className="panel">
          <Empty
            title="Your data belongs here"
            description="Import CSV, TSV, delimited text, or an XLSX workbook. No database required."
          />
        </section>
      ) : (
        <>
          <div className="bulk-top">
            <section className="panel padded mapping-panel">
              <div className="section-heading">
                <h3>
                  <FileSpreadsheet size={18} />
                  Column mapping
                </h3>
                <span className="badge">{sheet.rows.length} records</span>
              </div>
              {sheets.length > 1 && (
                <Control label="Worksheet">
                  <Select
                    disabled={busy}
                    value={sheetIndex}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setSheet(n);
                      suggest(sheets[n]);
                    }}
                  >
                    {sheets.map((s, i) => (
                      <option key={i} value={i}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </Control>
              )}
              <div className="mapping-list">
                {sheet.headers.map((header, i) => (
                  <div key={i}>
                    <span>{header}</span>
                    <span>→</span>
                    <Select
                      disabled={busy}
                      aria-label={`Map ${header}`}
                      value={mapping[i] || ""}
                      onChange={(e) => {
                        setMapping({ ...mapping, [i]: e.target.value });
                        setConfirmed(false);
                        setRows([]);
                        setBatch(undefined);
                        setReviewProject(undefined);
                        setReviewValues(undefined);
                      }}
                    >
                      <option value="">Skip column</option>
                      {effectiveFields(proofProject, partId || undefined)
                        .filter(bulkFieldEligible)
                        .map((f) => (
                          <option
                            key={f.id}
                            value={f.id}
                            disabled={Object.entries(mapping).some(
                              ([k, v]) => Number(k) !== i && v === f.id,
                            )}
                          >
                            {f.label}
                          </option>
                        ))}
                    </Select>
                  </div>
                ))}
              </div>
              <Button
                kind={confirmed ? "" : "primary"}
                disabled={
                  busy ||
                  host.blocked ||
                  parseDirty ||
                  !Object.values(mapping).some(Boolean)
                }
                onClick={() => void confirmMapping()}
              >
                <CheckCircle2 size={15} />
                {busy && !confirmed
                  ? "Importing image URLs…"
                  : confirmed
                    ? "Mappings confirmed"
                    : "Confirm column mappings"}
              </Button>
            </section>
            <section className="panel padded batch-settings">
              <h3>Batch settings</h3>
              {host.needs && (
                <Control
                  label={
                    contextProfiles?.length ? "Context profile" : "Host values"
                  }
                >
                  <Select
                    disabled={busy}
                    hidden={!contextProfiles?.length}
                    value={demoProfile}
                    onChange={(e) => {
                      setDemoProfile(e.target.value);
                      host.refresh();
                    }}
                  >
                    {contextProfiles?.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.label}
                      </option>
                    ))}
                  </Select>

                  {!host.current && (
                    <p role="status">Loading profile values…</p>
                  )}
                  {host.current?.error && (
                    <p role="alert">{host.current.error}</p>
                  )}
                </Control>
              )}

              <Control label="Output profile">
                <Select
                  value={profile.id}
                  disabled={!!batch || busy}
                  onChange={(e) => setProfile(e.target.value)}
                >
                  {proofProject.outputs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              </Control>
              <div className="batch-statistics">
                <div>
                  <strong>
                    {validations.filter((v) => !v.errors.length).length}
                  </strong>
                  <span>Ready</span>
                </div>
                <div>
                  <strong>
                    {validations.filter((v) => v.errors.length).length}
                  </strong>
                  <span>Needs attention</span>
                </div>
              </div>
              <Button
                kind="primary full"
                disabled={
                  !confirmed ||
                  parseDirty ||
                  host.blocked ||
                  busy ||
                  rows.some(
                    (r) => r.job && ["queued", "running"].includes(r.job.state),
                  )
                }
                onClick={() => void start()}
              >
                <Play size={15} />
                Generate valid records
              </Button>
              {rows.length > 0 && (
                <>
                  <progress
                    max={batch?.progress.total || rows.length}
                    value={batch?.progress.processed ?? progress}
                  />
                  <p>
                    {batch?.progress.processed ?? progress} /{" "}
                    {batch?.progress.total || rows.length} processed
                  </p>
                  <Button
                    disabled={
                      busy || !rows.some((r) => r.job?.state === "complete")
                    }
                    onClick={() => void zip()}
                  >
                    <Download size={15} />
                    Download completed ZIP
                  </Button>
                  <Button disabled={busy} onClick={() => void cancelBatch()}>
                    <X size={15} />
                    Cancel pending records
                  </Button>
                </>
              )}
            </section>
          </div>
          <section className="panel bulk-review">
            <header>
              <h3>Row-by-row proof review</h3>
              <Select
                disabled={busy}
                aria-label="Filter batch rows"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
              >
                <option value="all">All rows</option>
                <option value="invalid">Invalid rows</option>
                <option value="failed">Failed rows</option>
                <option value="complete">Completed rows</option>
              </Select>
            </header>
            <div className="bulk-review-body">
              <div className="bulk-preview">
                <ProofCanvas
                  {...previews}
                  project={proofProject}
                  context={context}
                  values={values[index] || {}}
                  profile={profile}
                  pageIndex={pageIndices[index] || 0}
                  onPageCount={setPageCount}
                  partId={partId || undefined}
                  zoom={0.6}
                />
              </div>
              <div className="bulk-row-details">
                <div className="row-navigation">
                  <button
                    aria-label="Previous record"
                    disabled={index === 0}
                    onClick={() => navigate(-1)}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <strong aria-live="polite">
                    Bulk Proof {index + 1} of {values.length}
                  </strong>
                  <button
                    aria-label="Next record"
                    disabled={index === values.length - 1}
                    onClick={() => navigate(1)}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
                <div className="row-navigation">
                  <Button
                    disabled={(pageIndices[index] || 0) === 0}
                    onClick={() =>
                      setPageIndices((current) => ({
                        ...current,
                        [index]: (current[index] || 0) - 1,
                      }))
                    }
                  >
                    Previous proof page
                  </Button>
                  <span>
                    {(pageIndices[index] || 0) + 1} / {pageCount}
                  </span>
                  <Button
                    disabled={(pageIndices[index] || 0) >= pageCount - 1}
                    onClick={() =>
                      setPageIndices((current) => ({
                        ...current,
                        [index]: (current[index] || 0) + 1,
                      }))
                    }
                  >
                    Next proof page
                  </Button>
                </div>
                <Control label="Go to failed or invalid record">
                  <Select
                    disabled={busy}
                    value=""
                    onChange={(e) => setIndex(Number(e.target.value))}
                  >
                    <option value="">Select a record</option>
                    {rows.map((row, i) =>
                      row.state === "failed" || row.state === "invalid" ? (
                        <option key={i} value={i}>
                          {row.state} · source row{" "}
                          {sheet.sourceRows?.[i] || i + 2} · proof {i + 1}
                        </option>
                      ) : null,
                    )}
                  </Select>
                </Control>
                <Control label="Go to row">
                  <input
                    type="number"
                    min="1"
                    max={values.length}
                    value={index + 1}
                    onChange={(e) =>
                      setIndex(
                        Math.max(
                          0,
                          Math.min(
                            values.length - 1,
                            Number(e.target.value) - 1,
                          ),
                        ),
                      )
                    }
                  />
                </Control>
                {sheet.headers.map((h, i) => (
                  <div className="data-pair" key={i}>
                    <span>{h}</span>
                    <strong>{sheet.rows[index]?.[i] || "—"}</strong>
                  </div>
                ))}
                {validations[index]?.errors.map((e, i) => (
                  <p className="error-text" key={i}>
                    {e.message}
                  </p>
                ))}
                {rows[index]?.job && (
                  <div className="artifact-card">
                    <strong>{rows[index].job!.state}</strong>
                    {rows[index].job!.error && <p>{rows[index].job!.error}</p>}
                    {rows[index].job!.state === "complete" &&
                      rows[index].job!.artifact?.downloadAllowed && (
                        <a
                          className="button primary"
                          role="link"
                          tabIndex={0}
                          aria-disabled={busy}
                          onClick={(event) => {
                            event.preventDefault();
                            if (!busy) void downloadRecord(rows[index].job!);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" && !busy)
                              void downloadRecord(rows[index].job!);
                          }}
                        >
                          Download this record
                        </a>
                      )}
                    {rows[index].retryable && (
                      <Button disabled={busy} onClick={() => void retryRow()}>
                        <RotateCcw size={14} />
                        Retry record
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
          <section className="panel data-preview">
            <h3>Imported data preview</h3>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Row</th>
                    {sheet.headers.map((h, i) => (
                      <th key={i}>{h}</th>
                    ))}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sheet.rows.slice(0, 5).map((row, i) => (
                    <tr
                      key={i}
                      className={i === index ? "selected" : ""}
                      onClick={() => setIndex(i)}
                    >
                      <td>{i + 1}</td>
                      {sheet.headers.map((_, j) => (
                        <td key={j}>{row[j]}</td>
                      ))}
                      <td>
                        <span
                          className={`badge ${validations[i].errors.length ? "red" : "green"}`}
                        >
                          {rows[i]?.job?.state ||
                            (validations[i].errors.length
                              ? "Invalid"
                              : "Ready")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
